import * as THREE from "three";

/**
 * Generateurs de textures procedurales (canvas 2D) pour l'ambiance
 * "taverne medievale" : bois veine, pierre, parchemin, flamme.
 * Repris de dkg-farkle (memes algorithmes) : zero asset externe = zero risque
 * de licence, et un poids de build minuscule.
 */

export function makeCanvas(size: number): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  return makeCanvasWH(size, size);
}

export function makeCanvasWH(
  width: number,
  height: number,
): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas 2D non disponible");
  return { canvas, ctx };
}

export function mulberry32(seed: number) {
  let a = seed;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function toTexture(canvas: HTMLCanvasElement, repeat = 1): THREE.CanvasTexture {
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
  tex.repeat.set(repeat, repeat);
  tex.colorSpace = THREE.SRGBColorSpace;
  // 2 plutot que 4 : la camera, quasi verticale (cf. render/scene.ts), voit
  // ces surfaces avec peu d'obliquite — le filtrage anisotrope y apporte peu,
  // pour un cout de bande passante qui compte sur un GPU mobile.
  tex.anisotropy = 2;
  return tex;
}

/** Planches de bois sombre veine, pour la table et le decor. */
export function generateWoodTexture(options?: { seed?: number; baseColor?: string; plankCount?: number }): {
  map: THREE.CanvasTexture;
  roughnessMap: THREE.CanvasTexture;
} {
  const size = 1024;
  const seed = options?.seed ?? 1;
  const rng = mulberry32(seed);
  const plankCount = options?.plankCount ?? 6;
  const { canvas, ctx } = makeCanvas(size);

  const base = options?.baseColor ?? "#5a3a22";
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, size, size);

  const plankHeight = size / plankCount;
  for (let p = 0; p < plankCount; p++) {
    const y0 = p * plankHeight;
    // Chaque planche a sa propre patine : ecarts de teinte marques (bois use, rustique).
    const shade = 0.62 + rng() * 0.68;
    ctx.fillStyle = shadeColor(base, shade);
    ctx.fillRect(0, y0, size, plankHeight);

    // Veines de bois : courbes horizontales ondulantes.
    const veinCount = 7 + Math.floor(rng() * 6);
    for (let v = 0; v < veinCount; v++) {
      const yStart = y0 + rng() * plankHeight;
      ctx.strokeStyle = shadeColor(base, 0.35 + rng() * 0.25);
      ctx.lineWidth = 1 + rng() * 2.5;
      ctx.globalAlpha = 0.3 + rng() * 0.35;
      ctx.beginPath();
      ctx.moveTo(0, yStart);
      let y = yStart;
      for (let x = 0; x <= size; x += 24) {
        y += (rng() - 0.5) * 14;
        y = Math.max(y0 + 2, Math.min(y0 + plankHeight - 2, y));
        ctx.lineTo(x, y);
      }
      ctx.stroke();
    }

    // Fentes de sechage : fines craquelures sombres et nettes (contraste plus
    // fort que les veines), le long du fil du bois — le cote "bois brut" du
    // rustique n'est pas qu'une histoire de teinte.
    const crackCount = rng() < 0.75 ? 1 + Math.floor(rng() * 2) : 0;
    for (let c = 0; c < crackCount; c++) {
      let cx = rng() * size;
      const cy0 = y0 + rng() * plankHeight;
      ctx.strokeStyle = "#140a04";
      ctx.lineWidth = 0.8 + rng() * 1.4;
      ctx.globalAlpha = 0.45 + rng() * 0.3;
      ctx.beginPath();
      let cy = cy0;
      ctx.moveTo(cx, cy);
      const len = size * (0.15 + rng() * 0.55);
      const dir = rng() < 0.5 ? -1 : 1;
      for (let d = 0; d < len; d += 10 + rng() * 10) {
        cx += dir * (10 + rng() * 10);
        cy += (rng() - 0.5) * 5;
        cy = Math.max(y0 + 1, Math.min(y0 + plankHeight - 1, cy));
        ctx.lineTo(cx, cy);
      }
      ctx.stroke();
    }

    // Noeuds de bois.
    const knotCount = rng() < 0.5 ? 2 : 1;
    for (let k = 0; k < knotCount; k++) {
      const kx = rng() * size;
      const ky = y0 + plankHeight / 2 + (rng() - 0.5) * plankHeight * 0.5;
      const kr = 7 + rng() * 13;
      const grad = ctx.createRadialGradient(kx, ky, 0, kx, ky, kr);
      grad.addColorStop(0, shadeColor(base, 0.2));
      grad.addColorStop(0.55, shadeColor(base, 0.4));
      grad.addColorStop(1, "rgba(0,0,0,0)");
      ctx.globalAlpha = 0.7;
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.ellipse(kx, ky, kr, kr * 1.6, rng() * Math.PI, 0, Math.PI * 2);
      ctx.fill();
    }

    // Taches d'usure (biere renversee, chandelles) et entailles.
    if (rng() < 0.6) {
      const sx = rng() * size;
      const sy = y0 + rng() * plankHeight;
      const sr = 10 + rng() * 26;
      const stain = ctx.createRadialGradient(sx, sy, 0, sx, sy, sr);
      stain.addColorStop(0, "rgba(15,8,3,0.45)");
      stain.addColorStop(1, "rgba(15,8,3,0)");
      ctx.globalAlpha = 1;
      ctx.fillStyle = stain;
      ctx.beginPath();
      ctx.ellipse(sx, sy, sr, sr * (0.5 + rng() * 0.4), rng() * Math.PI, 0, Math.PI * 2);
      ctx.fill();
    }
    const gougeCount = Math.floor(rng() * 3);
    for (let g = 0; g < gougeCount; g++) {
      const gx = rng() * size;
      const gy = y0 + rng() * plankHeight;
      const angle = rng() * Math.PI;
      const len = 8 + rng() * 22;
      ctx.globalAlpha = 0.25 + rng() * 0.25;
      ctx.strokeStyle = rng() < 0.5 ? shadeColor(base, 0.25) : shadeColor(base, 1.5);
      ctx.lineWidth = 0.6 + rng() * 1.2;
      ctx.beginPath();
      ctx.moveTo(gx - Math.cos(angle) * len, gy - Math.sin(angle) * len);
      ctx.lineTo(gx + Math.cos(angle) * len, gy + Math.sin(angle) * len);
      ctx.stroke();
    }

    // Joint sombre entre planches.
    ctx.globalAlpha = 0.85;
    ctx.fillStyle = "#000000";
    ctx.fillRect(0, y0, size, 2.5);
  }
  ctx.globalAlpha = 1;

  // Grain fin (bruit) par-dessus tout.
  const grain = ctx.getImageData(0, 0, size, size);
  for (let i = 0; i < grain.data.length; i += 4) {
    const n = (rng() - 0.5) * 30;
    grain.data[i]! += n;
    grain.data[i + 1]! += n;
    grain.data[i + 2]! += n;
  }
  ctx.putImageData(grain, 0, 0);

  const map = toTexture(canvas, 3);

  // Carte de rugosite : planches legerement variables, joints plus mats.
  const { canvas: rCanvas, ctx: rCtx } = makeCanvas(size);
  rCtx.fillStyle = "#8a8a8a";
  rCtx.fillRect(0, 0, size, size);
  for (let p = 0; p < plankCount; p++) {
    const y0 = p * plankHeight;
    const v = 120 + Math.floor(rng() * 60);
    rCtx.fillStyle = `rgb(${v},${v},${v})`;
    rCtx.fillRect(0, y0, size, plankHeight);
    rCtx.fillStyle = "#404040";
    rCtx.fillRect(0, y0, size, 2);
  }
  const roughnessMap = toTexture(rCanvas, 3);

  return { map, roughnessMap };
}

/** Murs/sol de pierre appareillee (moellons en assises decalees, joints de mortier). */
export function generateStoneTexture(options?: { seed?: number; baseColor?: string }): {
  map: THREE.CanvasTexture;
  roughnessMap: THREE.CanvasTexture;
} {
  const size = 1024;
  const seed = options?.seed ?? 3;
  const rng = mulberry32(seed);
  const base = options?.baseColor ?? "#4d473f";
  const rowHeight = size / 10;
  const { canvas, ctx } = makeCanvas(size);

  // Fond = couleur des joints de mortier, les moellons sont peints par-dessus.
  ctx.fillStyle = shadeColor(base, 0.55);
  ctx.fillRect(0, 0, size, size);

  let row = 0;
  for (let y = 0; y < size; y += rowHeight) {
    // Assises decalees une rangee sur deux (appareil "a joints croises").
    const offset = row % 2 === 0 ? 0 : rowHeight * 0.9;
    let x = -offset;
    while (x < size) {
      const w = rowHeight * (1.4 + rng() * 1.1);
      const pad = 2 + rng() * 2;
      const shade = 0.75 + rng() * 0.55;
      ctx.fillStyle = shadeColor(base, shade);
      ctx.fillRect(x + pad, y + pad, Math.max(1, w - pad * 2), rowHeight - pad * 2);

      if (rng() < 0.4) {
        const sx = x + pad + rng() * Math.max(1, w - pad * 2);
        const sy = y + pad + rng() * (rowHeight - pad * 2);
        const sr = 5 + rng() * 12;
        const stain = ctx.createRadialGradient(sx, sy, 0, sx, sy, sr);
        stain.addColorStop(0, "rgba(0,0,0,0.3)");
        stain.addColorStop(1, "rgba(0,0,0,0)");
        ctx.fillStyle = stain;
        ctx.beginPath();
        ctx.ellipse(sx, sy, sr, sr * 0.7, rng() * Math.PI, 0, Math.PI * 2);
        ctx.fill();
      }

      x += w;
    }
    row++;
  }

  const grain = ctx.getImageData(0, 0, size, size);
  for (let i = 0; i < grain.data.length; i += 4) {
    const n = (rng() - 0.5) * 24;
    grain.data[i]! += n;
    grain.data[i + 1]! += n;
    grain.data[i + 2]! += n;
  }
  ctx.putImageData(grain, 0, 0);

  const map = toTexture(canvas, 1);

  const { canvas: rCanvas, ctx: rCtx } = makeCanvas(size);
  rCtx.fillStyle = "#4a4a4a";
  rCtx.fillRect(0, 0, size, size);
  let rRow = 0;
  for (let y = 0; y < size; y += rowHeight) {
    const offset = rRow % 2 === 0 ? 0 : rowHeight * 0.9;
    let x = -offset;
    while (x < size) {
      const w = rowHeight * (1.4 + rng() * 1.1);
      const pad = 2 + rng() * 2;
      const v = 150 + Math.floor(rng() * 60);
      rCtx.fillStyle = `rgb(${v},${v},${v})`;
      rCtx.fillRect(x + pad, y + pad, Math.max(1, w - pad * 2), rowHeight - pad * 2);
      x += w;
    }
    rRow++;
  }
  const roughnessMap = toTexture(rCanvas, 1);

  return { map, roughnessMap };
}

/** Parchemin use, base des faces de cartes et des panneaux d'UI. */
export function drawParchment(ctx: CanvasRenderingContext2D, width: number, height: number, seed = 7): void {
  const rng = mulberry32(seed);
  const grad = ctx.createLinearGradient(0, 0, width, height);
  grad.addColorStop(0, "#e8d3a0");
  grad.addColorStop(0.5, "#dcc088");
  grad.addColorStop(1, "#cbaa6f");
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, width, height);

  for (let i = 0; i < 320; i++) {
    ctx.globalAlpha = 0.04 + rng() * 0.08;
    ctx.fillStyle = rng() < 0.5 ? "#3a2a15" : "#fff8e0";
    const r = 4 + rng() * 30;
    ctx.beginPath();
    ctx.ellipse(rng() * width, rng() * height, r, r * (0.4 + rng() * 0.6), rng() * Math.PI, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;

  // Bords brunis.
  const edge = ctx.createRadialGradient(
    width / 2,
    height / 2,
    Math.min(width, height) * 0.35,
    width / 2,
    height / 2,
    Math.max(width, height) * 0.72,
  );
  edge.addColorStop(0, "rgba(0,0,0,0)");
  edge.addColorStop(1, "rgba(60,35,10,0.55)");
  ctx.fillStyle = edge;
  ctx.fillRect(0, 0, width, height);
}

/** Parchemin en texture seule (panneaux, butin). */
export function generateParchmentTexture(): THREE.CanvasTexture {
  const { canvas, ctx } = makeCanvas(512);
  drawParchment(ctx, 512, 512);
  return toTexture(canvas, 1);
}

/**
 * Feutrine/cuir tendu, tapis de jeu rouge (repris de dkg-farkle, meme
 * algorithme et meme teinte que le fond de sa boite) : grain bruite, avec
 * vignette optionnelle vers les bords (a n'activer que pour un tapis affiche
 * en un seul exemplaire — sur une surface tuilee/repetee, cf. `repeat` dans
 * l'appelant, la vignette redessinerait un quadrillage de zones sombres).
 */
export function generateFeltTexture(colorHex = "#5f1f22", withVignette = true): THREE.CanvasTexture {
  const size = 512;
  const rng = mulberry32(42);
  const { canvas, ctx } = makeCanvas(size);
  ctx.fillStyle = colorHex;
  ctx.fillRect(0, 0, size, size);

  const img = ctx.getImageData(0, 0, size, size);
  for (let i = 0; i < img.data.length; i += 4) {
    const n = (rng() - 0.5) * 22;
    img.data[i]! += n;
    img.data[i + 1]! += n;
    img.data[i + 2]! += n;
  }
  ctx.putImageData(img, 0, 0);

  if (withVignette) {
    const grad = ctx.createRadialGradient(size / 2, size / 2, size * 0.2, size / 2, size / 2, size * 0.7);
    grad.addColorStop(0, "rgba(0,0,0,0)");
    grad.addColorStop(1, "rgba(0,0,0,0.35)");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, size, size);
  }

  return toTexture(canvas, 1);
}

/**
 * Silhouette de flamme (blanche sur transparent) pour sprite additif de
 * bougie/cheminee : lobes radiaux empiles, retrecissant vers le haut, pour
 * un profil "goutte". La teinte finale (orange) vient du SpriteMaterial.color
 * en blending additif, pas de ce canvas.
 */
export function generateFlameTexture(): THREE.CanvasTexture {
  const size = 128;
  const { canvas, ctx } = makeCanvas(size);
  ctx.clearRect(0, 0, size, size);

  const cx = size / 2;
  const lobes = [
    { cy: 0.86, rx: 0.34, ry: 0.28, alpha: 0.95 },
    { cy: 0.62, rx: 0.26, ry: 0.34, alpha: 0.85 },
    { cy: 0.4, rx: 0.17, ry: 0.32, alpha: 0.75 },
    { cy: 0.22, rx: 0.09, ry: 0.22, alpha: 0.65 },
  ];
  for (const lobe of lobes) {
    const cy = size * lobe.cy;
    const rx = size * lobe.rx;
    const ry = size * lobe.ry;
    const r = Math.max(rx, ry);
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    grad.addColorStop(0, `rgba(255,255,255,${lobe.alpha})`);
    grad.addColorStop(0.55, `rgba(255,255,255,${lobe.alpha * 0.5})`);
    grad.addColorStop(1, "rgba(255,255,255,0)");
    ctx.save();
    ctx.translate(cx, cy);
    ctx.scale(rx / r, ry / r);
    ctx.translate(-cx, -cy);
    ctx.fillStyle = grad;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

export function shadeColor(hex: string, factor: number): string {
  const c = new THREE.Color(hex);
  c.multiplyScalar(factor);
  return `#${c.getHexString()}`;
}
